/**
 * DashboardSelector — dropdown in the top header bar for quick dashboard navigation.
 * Shows all dashboards the user has access to, grouped by category.
 * Uses TanStack Query for shared caching — multiple instances won't duplicate requests.
 */
import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { BarChart3, ChevronDown, Search } from 'lucide-react';
import { useAuthStore } from '@/stores/auth';
import { listDashboards } from '@/lib/bi/dashboard-api';
import './DashboardSelector.css';

export function DashboardSelector() {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const envId = useAuthStore((s) => s.user?.active_environment_id);

  // Shared query — cached across all DashboardSelector instances
  const { data: dashboards = [] } = useQuery({
    queryKey: ['dashboards', envId],
    queryFn: listDashboards,
    staleTime: 60_000,
  });

  // Extract current dashboard ID from URL
  const currentDashboardId = useMemo(() => {
    const match = location.pathname.match(/^\/bi\/([^/?]+)/);
    return match?.[1] || null;
  }, [location.pathname]);

  const currentDashboard = useMemo(
    () => dashboards.find((d) => d.id === currentDashboardId),
    [dashboards, currentDashboardId],
  );

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch('');
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  // Group by category and filter by search
  const grouped = useMemo(() => {
    const term = search.toLowerCase();
    const filtered = term
      ? dashboards.filter(
          (d) => d.name.toLowerCase().includes(term) || (d.description || '').toLowerCase().includes(term),
        )
      : dashboards;

    const groups = new Map<string, typeof dashboards>();
    for (const d of filtered) {
      const cat = d.category || '';
      if (!groups.has(cat)) groups.set(cat, []);
      groups.get(cat)!.push(d);
    }
    return groups;
  }, [dashboards, search]);

  if (dashboards.length === 0) return null;

  return (
    <div className="dashboard-selector" ref={ref}>
      <button
        className={`dashboard-selector-btn ${currentDashboard ? 'active' : ''}`}
        onClick={() => {
          setOpen(!open);
          setSearch('');
        }}
        aria-expanded={open}
        aria-haspopup="menu"
      >
        <BarChart3 size={14} />
        <span className="dashboard-selector-label">
          {currentDashboard ? currentDashboard.name : 'Dashboards'}
        </span>
        <ChevronDown size={12} className={`dashboard-selector-chevron ${open ? 'open' : ''}`} />
      </button>

      {open && (
        <div className="dashboard-selector-dropdown" role="menu">
          {dashboards.length > 5 && (
            <div className="dashboard-selector-search">
              <Search size={12} />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search dashboards..."
                aria-label="Search dashboards"
                autoFocus
              />
            </div>
          )}

          {/* All Dashboards link */}
          <button
            role="menuitem"
            className={`dashboard-selector-item ${location.pathname === '/bi' ? 'active' : ''}`}
            onClick={() => {
              void navigate('/bi');
              setOpen(false);
              setSearch('');
            }}
          >
            <span className="dashboard-selector-item-name">All Dashboards</span>
          </button>

          <div className="dashboard-selector-divider" />

          {[...grouped.entries()].map(([category, items]) => (
            <div key={category || '__uncategorized'}>
              {category && <div className="dashboard-selector-category">{category}</div>}
              {items.map((d) => (
                <button
                  key={d.id}
                  role="menuitem"
                  className={`dashboard-selector-item ${d.id === currentDashboardId ? 'active' : ''}`}
                  onClick={() => {
                    void navigate(`/bi/${d.id}`);
                    setOpen(false);
                    setSearch('');
                  }}
                >
                  <span className="dashboard-selector-item-name">{d.name}</span>
                  {d.description && (
                    <span className="dashboard-selector-item-desc">
                      {d.description.length > 50 ? d.description.slice(0, 48) + '...' : d.description}
                    </span>
                  )}
                </button>
              ))}
            </div>
          ))}

          {grouped.size === 0 && search && (
            <div className="dashboard-selector-empty">No dashboards match &quot;{search}&quot;</div>
          )}
        </div>
      )}
    </div>
  );
}
